<template>
  <div class="candidate-log-create-form">
    <div
      class="candidate-log-create-form__textarea"
      :class="{ 'mb-3': isOfficeInterviewRecorded || isOnlineInterviewRecorded }"
    >
      <VTextarea
        v-model="comment"
        outlined
        :rows="$vuetify.breakpoint.mdAndUp ? 3 : 5"
        :error-messages="errors.comment"
        background-color="#fff"
        class="body-2 textarea-send"
        @keydown.enter="submit"
      >
        <template #label>
          <span class="body-2">
            {{ $t('commentTip') }}
          </span>
        </template>
        <template v-if="$vuetify.breakpoint.mdAndUp" #append>
          <VBtn
            x-small
            depressed
            class="candidate-log-create-form__send-comment mb-3"
            color="success"
            :loading="loading"
            :disabled="loading || !comment"
            @click="submit"
          >
            <VIcon>
              M17.5,6.5c-0.3,0-0.5,0.1-0.7,0.3c-0.2,0.2-0.3,0.4-0.3,0.7v4c0,0.3-0.1,
              0.5-0.3,0.7c-0.2,0.2-0.4,0.3-0.7,0.3
              H8.9l1.3-1.3c0.2-0.2,0.3-0.4,0.3-0.7s-0.1-0.5-0.3-0.7C10,
              9.6,9.8,9.5,9.5,9.5C9.2,9.5,9,9.6,8.8,9.8l-3,3
              c-0.1,0.1-0.2,0.2-0.2,0.3c-0.1,0.2-0.1,0.5,0,0.8c0,0.1,0.1,0.2,0.2,
              0.3l3,3c0.1,0.1,0.2,0.2,0.3,0.2c0.1,0.1,0.3,0.1,0.4,0.1
              c0.1,0,0.3,0,0.4-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.1-0.1,0.2-0.2,
              0.2-0.3c0.1-0.1,0.1-0.3,0.1-0.4c0-0.1,0-0.3-0.1-0.4
              c-0.1-0.1-0.1-0.2-0.2-0.3l-1.3-1.3h6.6c0.8,0,1.6-0.3,
              2.1-0.9c0.6-0.6,0.9-1.3,0.9-2.1v-4c0-0.3-0.1-0.5-0.3-0.7 C18,6.6,17.8,6.5,17.5,6.5z
            </VIcon>
          </VBtn>
        </template>
      </VTextarea>
    </div>
    <VCheckbox
      v-if="isOfficeInterviewRecorded || isOnlineInterviewRecorded"
      v-model="bindAdditionalData"
      hide-details
      :disabled="isOnlineInterviewRecorded && !currentOnlineInterviewQuestion"
      :label="$t('bindToTimestamp')"
    >
      <template #label>
        <span v-if="isOnlineInterviewRecorded" class="body-2">
          {{ $t('bindToAnswer') }}
        </span>
        <span v-if="isOfficeInterviewRecorded" class="body-2">
          {{ $t('bindToTimestamp') }} {{ officeInterviewPlayerTimeFormatted }}
        </span>
      </template>
    </VCheckbox>
    <VBtn
      v-if="$vuetify.breakpoint.smAndDown"
      color="success"
      :loading="loading"
      :disabled="loading || !comment"
      class="mt-3"
      @click="submit"
    >
      {{ $t('send') }}
    </VBtn>
  </div>
</template>

<script>
import { candidateLogType, interviewType, interviewStatusType } from '@/enums';
import ValidationMixin from '@/mixins/ValidationMixin';
import { readableSeconds } from '@/functions';
import CandidateLog from '@/orm/models/Candidate/CandidateLog';
import OfficeInterviewLog from '@/orm/models/OfficeInterview/OfficeInterviewLog';
import InterviewLog from '@/orm/models/Interview/InterviewLog';
import Vacancy from '@/orm/models/Vacancy/Vacancy';

export default {
  name: 'CandidateLogCreateForm',
  mixins: [ValidationMixin],
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    interviewType: {
      type: String,
      required: true,
    },
    officeInterviewPlayerTime: {
      type: Number,
      default: 0,
    },
    onlineInterviewQuestionIndex: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      comment: null,
      loading: false,
      bindAdditionalData: false,
    };
  },
  computed: {
    isOnlineInterviewRecorded() {
      return (
        this.interviewType === interviewType.ONLINE_INTERVIEW
        && this.candidate.interview
        && this.candidate.interview.status === interviewStatusType.READY
      );
    },
    isOfficeInterviewRecorded() {
      return (
        this.interviewType === interviewType.OFFICE_INTERVIEW
        && this.candidate.officeInterview
        && this.candidate.officeInterview.status === interviewStatusType.READY
      );
    },
    officeInterviewPlayerTimeFormatted() {
      return readableSeconds(this.officeInterviewPlayerTime);
    },
    currentOnlineInterviewQuestion() {
      if (this.onlineInterviewQuestionIndex === null) {
        return null;
      }

      const vacancy = Vacancy.find(this.candidate.vacancyId);

      return vacancy.script.questions[this.onlineInterviewQuestionIndex];
    },
  },
  methods: {
    submit() {
      this.withValidationHandling(async () => {
        if (this.isOnlineInterviewRecorded && this.bindAdditionalData) {
          await this.storeOnlineInterviewLog();
        } else if (this.isOfficeInterviewRecorded && this.bindAdditionalData) {
          await this.storeOfficeInterviewLog();
        } else {
          await this.storeCandidateLog();
        }
        this.comment = null;
        this.bindAdditionalData = false;
        this.$emit('log-entry-created');
        this.$emit('change-active-tab', 0);
      });
    },
    async storeCandidateLog() {
      await CandidateLog.api().store(this.candidate.id, {
        eventType: candidateLogType.COMMENT,
        comment: this.comment,
      });
    },
    async storeOnlineInterviewLog() {
      await InterviewLog.api().store(this.candidate.interview.id, {
        comment: this.comment,
        questionId: this.currentOnlineInterviewQuestion.id,
        questionType: this.currentOnlineInterviewQuestion.type,
      });
    },
    async storeOfficeInterviewLog() {
      await OfficeInterviewLog.api().store(this.candidate.officeInterview.id, {
        comment: this.comment,
        timestamp: this.officeInterviewPlayerTime,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.candidate-log-create-form__textarea {
  &::v-deep {
    .v-textarea {
      .v-input__control {
        .v-input__slot {
          max-height: 70px;
          margin-bottom: 0;
        }
        .v-text-field__details {
          display: none;
        }
      }
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  commentTip: Your comment (press Enter to send)
  bindToTimestamp: Attach to timestamp
  bindToAnswer: Attach to answer
  send: Submit
</i18n>

<i18n locale="ru" lang="yaml">
  commentTip: Ваш комментарий (Enter — отправить)
  bindToTimestamp: Привязать к временной метке
  bindToAnswer: Привязать к ответу на вопрос
  send: Отправить
</i18n>
